import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Chevrolet Corvette C2 "Sting Ray" (1963 split-window coupe) at true scale
// (1 unit = 1 metre): length ~4.45, width 1.77, height 1.26 (very low),
// wheelbase 2.49 (front axle z=+1.05, rear z=-1.44), wheel radius ~0.33. Nose
// is +Z. Authored for the `cars` showcase batch. A LOW, curvy, aggressive
// American sports coupe: sharply PEAKED pointed fenders front and rear (a ridge
// crest along each wing top), a long low bonnet with twin power bulges dipping
// between the front fender crowns, a pointed low prow nose with HIDDEN headlamps
// (flush body-colour lids, only a faint dark seam) and a thin chrome grille
// slot, fake chrome fender gills behind the front wheels, a low shallow cabin
// set well back, and the 1963-only SIGNATURE: a continuous fastback whose raked
// rear glass is split into two panes by a thin body-colour spine. The tail is a
// low pointed Kamm with round twin tail lamps per side, a two-piece chrome
// bumper and twin exhausts. Knock-off five-spoke wheels. Body panels carry the
// "paint" recolour slot (Riverside Red); `drive` spins the wheels and `hazards`
// blinks the four indicators.

const PAINT = "#b3272d"; // Riverside Red — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (grille recess, pillars, vents, seams)
const CLAD = "#26282c"; // dark valances under the bumpers
const WELL = "#141518"; // wheel-well discs
const GLASS = "#1a2530"; // tinted side glazing
const SCREEN = "#2b3f52"; // windscreen / rear glass (lighter, reads as glass over the red)
const CABIN = "#101114"; // near-black interior block behind the glass
const CHROME = "#d0d5da"; // bright chrome — bumpers, grille slot, gills
const SPOKE = "#c4c9cf"; // bright alloy spokes / rim
const DISH = "#101114"; // dark wheel dish behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#eef4f8"; // parking-lamp glass
const RED = "#8f0f18"; // tail lamps, deeper than the red paint so they read
const REDDARK = "#5f0a10"; // tail-lamp inner
const AMBER = "#e8a13a"; // parking lamps / indicators
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.33;
const TYRE_W = 0.2;
const WHEEL_X = 0.78; // half track — tyre outer face (0.88) proud of the body
const AXLE_F = 1.05;
const AXLE_R = -1.44; // wheelbase 2.49
const BODY_X = 0.845; // arch-disc / main body half-width

// Continuous fastback roofline (mirrors porsche-911.ts): three tilted
// body-colour boxes whose top edges are colinear and OVERLAP, sweeping DOWN in
// one unbroken line from the low cabin roof to a LOW pointed tail — the rear
// deck ends well BELOW the roof, never rising into a raised deck. Angles
// decrease (steep backlight, flattening onto the tail) so the curve reads
// smooth with no notch and no flat patch, sweeping down to a LOW tail well
// below the roof. The cabin is set well back so the deck is SHORT and sweeps
// hard down (a dramatic fastback, not a long flat shelf). Breakpoints (z, y):
// (-0.60, 1.14) -> (-1.15, 0.92) -> (-1.65, 0.76) -> (-2.15, 0.60).
const SAIL_ANGLE = 0.38; // the raked rear window (the split backlight rides on this)
const LID_ANGLE = 0.31; // the deck keeps sweeping hard down
const DECK_ANGLE = 0.31; // still descending onto the low pointed tail (never flat, never rising)
const SCREEN_RAKE = 0.78; // steeply raked, low windscreen

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel box — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the pointed prow nose. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** A paint ridge crest: a box rolled 45 deg about its length (Z) so its top
 *  vertex forms the sharp PEAK line running along a fender, optionally pitched
 *  by `tiltX` to follow a sloping wing. No scale, so the combined rotation
 *  cannot shear it. The peak stands ~0.7*size above the box centre. Used for
 *  the C2's signature peaked pointed fenders. */
function crest(size: number, length: number, position: Vec3, tiltX = 0): Part {
  return box(size, size, length, PAINT, position, [tiltX, 0, Math.PI / 4], { slot: "paint" });
}

/** One knock-off five-spoke alloy: tyre, brake disc, dark recessed dish, five
 *  bright spokes proud of the tyre face, chrome centre cap and a chrome
 *  knock-off spinner cone pointing outward. Every part shares `name` so the
 *  `drive` track spins them as one node about the axle. `side` is +1 for the
 *  right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const outer = WHEEL_X + TYRE_W / 2; // tyre outer face at 0.88 — spokes ride just proud of it
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, TYRE_W, 20, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.14, 0.14, 0.03, 14, BRAKE, [side * (WHEEL_X - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.2, 0.2, 0.05, 18, DISH, [side * (outer - 0.055), y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.055, 0.36, 0.09, SPOKE, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.065, 0.065, 0.03, 12, CHROME, [side * (outer - 0.004), y, z], [0, 0, Math.PI / 2], { name })); // centre cap
  parts.push({ // knock-off spinner cone pointing outward
    geometry: new THREE.ConeGeometry(0.05, 0.09, 12),
    color: CHROME,
    position: [side * (outer + 0.03), y, z] as Vec3,
    rotation: [0, 0, -side * Math.PI / 2] as Vec3,
    name,
  });
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side plus a
 *  body-coloured half-torus brow over it (the peaked C2 flare is painted metal,
 *  not black cladding). The [0, pi/2, 0] yaw puts the 0..pi arc exactly over the
 *  top half; the tyre's outer face (0.88) stays proud of both. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.42, 0.42, 0.04, 20, WELL, [side * BODY_X, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.4, 0.05, 6, 20, Math.PI),
      color: PAINT,
      position: [side * (BODY_X + 0.02), WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair are the round amber parking lamps flanking the grille; rear pair
// are amber chips inboard of the round tail lamps.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.48, 0.48, 2.18], size: [0.07, 0.06, 0.04] },
  { name: "indicator-fr", pos: [0.48, 0.48, 2.18], size: [0.07, 0.06, 0.04] },
  { name: "indicator-rl", pos: [-0.18, 0.5, -2.27], size: [0.06, 0.06, 0.03] },
  { name: "indicator-rr", pos: [0.18, 0.5, -2.27], size: [0.06, 0.06, 0.03] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.5, 0.12, 4.0, TRIM, [0, 0.26, -0.1])); // underbody pan

  // -- Body masses (paint) ----------------------------------------------------
  P.push(paint(1.68, 0.42, 4.0, [0, 0.44, -0.05])); // lower tub, sill to belt (y 0.23..0.65)
  P.push(paint(1.58, 0.16, 2.6, [0, 0.7, 0.0])); // belt band, cowl to mid (y 0.62..0.78); stops before the tail drops below it

  // Long low bonnet dipping BETWEEN the peaked front fenders to the nose.
  P.push(paint(1.0, 0.12, 1.7, [0, 0.72, 1.2], [0.1, 0, 0])); // bonnet centre (dips low between the crowns)
  for (const s of S) {
    P.push(box(0.16, 0.05, 0.5, TRIM, [s * 0.2, 0.78, 1.5], [0.1, 0, 0])); // twin hood vent recess (clearly two)
    P.push(paint(0.24, 0.07, 1.1, [s * 0.2, 0.79, 1.3], [0.1, 0, 0])); // twin power bulge over the bonnet
  }

  // Peaked pointed front fenders: a raised crown box each side + a sharp ridge
  // crest along the top, running from the cowl forward to a point at the nose.
  for (const s of S) {
    P.push(paint(0.3, 0.2, 1.9, [s * 0.66, 0.76, 1.15], [0.05, 0, 0])); // front fender crown
    P.push(crest(0.13, 1.95, [s * 0.66, 0.8, 1.12], 0.05)); // crisp peak ridge to the cowl
  }

  // Pointed low prow nose: a nose block, a rounded prow set slightly high so it
  // points ABOVE the low grille slot, and a centre peak ridge to the tip.
  P.push(paint(1.5, 0.32, 0.34, [0, 0.56, 1.96])); // nose block behind the prow (front face ~2.13)
  P.push(blob([0.58, 0.16, 0.34], [0, 0.64, 1.94])); // rounded prow, tip at z ~2.28
  P.push(crest(0.12, 0.6, [0, 0.76, 1.96])); // centre nose peak ridge to the point

  // Rear haunches (the coke-bottle hip) fill the lower rear flanks, tops kept
  // LOW so the sloping fastback boxes above own the descending side silhouette.
  for (const s of S) P.push(paint(0.14, 0.3, 1.5, [s * 0.8, 0.48, -1.3])); // rear haunch (top ~0.63)

  // Low pointed tail panel (Kamm) — top ~0.64, well below the roof.
  P.push(paint(1.48, 0.3, 0.16, [0, 0.49, -2.14])); // tail panel (back face ~-2.22)

  // Fill the volume under the bumpers back to the wheel wells (no see-through).
  P.push(box(1.3, 0.2, 0.28, CLAD, [0, 0.32, 2.02])); // front valance
  P.push(box(1.3, 0.2, 0.28, CLAD, [0, 0.32, -2.02])); // rear valance

  // -- The unbroken fastback: sail + lid + tail deck (mirrors porsche-911) -----
  // Three tilted paint boxes whose top edges are colinear and overlap, sweeping
  // DOWN from the roof to the low tail. This is the WHOLE rear top surface;
  // there is NO raised block, NO flat patch and NO rising deck behind the cabin.
  P.push(paint(1.34, 0.42, 0.62, [0, 0.835, -0.875], [-SAIL_ANGLE, 0, 0])); // sail: the raked rear window
  P.push(paint(1.44, 0.36, 0.56, [0, 0.669, -1.4], [-LID_ANGLE, 0, 0])); // lid: sweeping hard down
  P.push(paint(1.5, 0.32, 0.56, [0, 0.528, -1.9], [-DECK_ANGLE, 0, 0])); // tail deck: sweeping onto the low tail

  // Peaked pointed REAR fenders: thin ridge crests riding the outer edges of the
  // fastback, only slightly proud (fender lines, NOT bed rails), following the
  // slope down to the tail.
  for (const s of S) {
    P.push(crest(0.1, 0.6, [s * 0.7, 1.0, -0.875], -SAIL_ANGLE)); // over the split backlight
    P.push(crest(0.1, 1.1, [s * 0.72, 0.67, -1.6], -LID_ANGLE)); // down the deck to the tail
  }

  // -- Greenhouse: a LOW, shallow cabin set well back, slim raked pillars ------
  P.push(box(1.0, 0.03, 0.1, TRIM, [0, 0.79, 0.38])); // cowl vent at the windscreen base
  P.push(box(1.1, 0.34, 0.98, CABIN, [0, 0.95, -0.25])); // dark interior block sealing the cabin (low, set back)
  P.push(box(1.2, 0.56, 0.05, SCREEN, [0, 0.96, 0.15], [-SCREEN_RAKE, 0, 0])); // low raked windscreen
  for (const s of S) P.push(paint(0.07, 0.56, 0.08, [s * 0.56, 0.96, 0.15], [-SCREEN_RAKE, 0, 0])); // slim raked A-pillars
  P.push(paint(1.12, 0.06, 0.5, [0, 1.14, -0.35])); // roof panel, low peak of the car (top ~1.17), set back
  for (const s of S) {
    P.push(box(0.03, 0.24, 0.58, GLASS, [s * 0.55, 0.94, -0.13], [0, 0, s * 0.14])); // low door glass, tumblehome in
    P.push(paint(0.07, 0.36, 0.14, [s * 0.53, 0.94, -0.62], [0, 0, s * 0.14])); // slim raked C-pillar bounding the split window
  }

  // -- The 1963-only SPLIT REAR WINDOW ----------------------------------------
  // Two raked glass panes lying ON the sail slope, divided by a THIN body-colour
  // spine down the centre. C-pillars bound the outer edges. From outside in:
  // C-pillar / pane / spine / pane / C-pillar — two panes on the sloping back.
  // Full-roof-width panes almost meeting at the centre spine, so they read as
  // ONE wide rear window split down the middle (not two separate "eyes").
  for (const s of S) {
    P.push(box(0.5, 0.03, 0.56, SCREEN, [s * 0.27, 1.02, -0.875], [-SAIL_ANGLE, 0, 0])); // split rear-window pane
  }
  P.push(paint(0.06, 0.06, 0.6, [0, 1.05, -0.875], [-SAIL_ANGLE, 0, 0])); // thin central spine splitting the glass (proud)
  P.push(box(0.96, 0.02, 0.04, CHROME, [0, 0.92, -1.15], [-SAIL_ANGLE, 0, 0])); // chrome window-base molding (frames the glass, breaks the "face")

  // -- Front fascia: hidden headlamps, chrome grille slot, parking lamps ------
  // Hidden headlamps: flush body-colour lids on the fender fronts, only a faint
  // DARK seam hinting the rotating lamp — the C2 nose is smooth, no exposed lamp.
  for (const s of S) {
    P.push(cyl(0.14, 0.14, 0.05, 16, PAINT, [s * 0.6, 0.66, 2.07], [Math.PI / 2, 0, 0], { slot: "paint" })); // flush lamp lid
    P.push({ // faint dark seam ring around the hidden lamp (not chrome)
      geometry: new THREE.TorusGeometry(0.14, 0.008, 6, 20),
      color: TRIM,
      position: [s * 0.6, 0.66, 2.1] as Vec3,
    });
  }
  P.push(box(0.84, 0.08, 0.05, TRIM, [0, 0.5, 2.14])); // dark grille recess behind the slot
  P.push(box(0.88, 0.045, 0.03, CHROME, [0, 0.5, 2.17])); // thin chrome grille slot (low, below the prow)
  for (const s of S) P.push(box(0.5, 0.09, 0.08, CHROME, [s * 0.42, 0.38, 2.16])); // two-piece front bumper
  P.push(box(0.34, 0.11, 0.03, PLATE, [0, 0.38, 2.2])); // front plate between the bumper halves
  for (const s of S) {
    P.push(cyl(0.055, 0.055, 0.04, 14, CHROME, [s * 0.48, 0.48, 2.16], [Math.PI / 2, 0, 0])); // parking-lamp bezel
    P.push(cyl(0.04, 0.04, 0.03, 14, LAMP, [s * 0.48, 0.48, 2.17], [Math.PI / 2, 0, 0])); // parking-lamp lens (amber indicator blinks proud)
  }

  // -- Fake fender gills: three chrome fins in a dark recess behind each front
  //    wheel arch (the iconic C2 side vent).
  for (const s of S) {
    P.push(box(0.03, 0.15, 0.28, TRIM, [s * 0.83, 0.62, 0.62])); // dark vent backing
    for (let i = 0; i < 3; i++) {
      P.push(box(0.04, 0.13, 0.03, CHROME, [s * 0.845, 0.62, 0.71 - i * 0.09], [0, s * 0.22, 0])); // angled chrome fins
    }
  }

  // -- Side coves: a chrome outline scallop along each flank (the C2 cove) -----
  for (const s of S) {
    P.push(box(0.02, 0.02, 1.4, CHROME, [s * 0.86, 0.7, 0.05])); // upper cove line
    P.push(box(0.02, 0.02, 1.4, CHROME, [s * 0.86, 0.54, 0.05])); // lower cove line
  }

  // -- Rear fascia: round twin tail lamps per side, two-piece chrome bumper ----
  for (const s of S) {
    for (const dx of [0.34, 0.56]) {
      P.push(cyl(0.09, 0.09, 0.05, 16, REDDARK, [s * dx, 0.5, -2.24], [Math.PI / 2, 0, 0])); // lamp base, proud of the tail
      P.push(cyl(0.075, 0.075, 0.04, 16, RED, [s * dx, 0.5, -2.27], [Math.PI / 2, 0, 0])); // red lens, proud
      P.push({ // chrome bezel ring
        geometry: new THREE.TorusGeometry(0.092, 0.012, 6, 18),
        color: CHROME,
        position: [s * dx, 0.5, -2.27] as Vec3,
      });
    }
  }
  for (const s of S) P.push(box(0.5, 0.09, 0.08, CHROME, [s * 0.42, 0.34, -2.24])); // two-piece rear bumper, proud
  P.push(box(0.34, 0.11, 0.03, PLATE, [0, 0.34, -2.28])); // rear plate between the bumper halves
  for (const s of S) P.push(cyl(0.035, 0.035, 0.16, 12, CHROME, [s * 0.24, 0.26, -2.22], [Math.PI / 2, 0, 0])); // twin exhausts

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.05, 0.02, 0.02, TRIM, [s * 0.8, 0.82, 0.34])); // mirror arm
    P.push(cyl(0.045, 0.045, 0.02, 12, CHROME, [s * 0.835, 0.84, 0.34], [0, 0, Math.PI / 2])); // mirror head
    P.push(box(0.02, 0.03, 0.11, CHROME, [s * 0.845, 0.64, -0.1])); // door handle
    P.push(box(0.22, 0.012, 0.03, TRIM, [s * 0.22, 0.78, 0.5], [-SCREEN_RAKE, 0, 0])); // wiper on the glass
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const chevroletCorvetteC2 = defineModel({
  id: "chevrolet-corvette-c2",
  name: "Chevrolet Corvette C2",
  batchId: "cars",
  tags: ["car", "chevrolet", "corvette", "stingray", "coupe", "sports", "classic"],
  summary: "A 1963 Chevrolet Corvette C2 Sting Ray split-window coupe at true scale, in Riverside Red.",
  description:
    "A 1963 Chevrolet Corvette C2 'Sting Ray' split-window coupe at real 1 unit " +
    "= 1 metre scale (about 4.45 long, 1.77 wide, 1.26 tall, 2.49 wheelbase). A " +
    "low, curvy, aggressive American sports coupe with all the cues: sharply " +
    "peaked pointed fenders front and rear with a ridge crest along each wing, a " +
    "long low bonnet with twin power bulges dipping between the front fender " +
    "crowns, a pointed low prow nose with hidden headlamps (flush body-colour " +
    "lids with only a faint dark seam) and a thin chrome grille slot, round amber " +
    "parking lamps, fake chrome fender gills, a chrome cove outline on the " +
    "flanks, a low shallow cabin set well back and the 1963-only signature: a " +
    "continuous fastback whose raked rear window is split into two panes by a " +
    "thin body-colour spine. The low pointed tail carries round twin tail lamps " +
    "per side on chrome bezels, a two-piece chrome bumper and twin exhausts, over " +
    "knock-off five-spoke alloys. Body panels carry the 'paint' recolour slot " +
    "(Riverside Red); `drive` spins the wheels and `hazards` blinks the four " +
    "indicators.",
  author: "Fable 5",
  footprint: 2.4,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
